import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import axios from "axios";

// selectors and hooks

export const selectUserById = userId => state => state.user.users.find(u => u.id === userId) || {};

export const selectUsers = state => state.user.users;

// Gets the users to pass as select options
export const useUserOptions = () => {
  return useSelector(state =>
    state.user.userOptions.map(user => ({ label: user.name, value: user.id }))
  );
};

export const useFieldVerifierOptions = () => {
  return useSelector(state =>
    state.user.users
      .filter(u => u.role !== "retailer")
      .map(user => ({ label: user.name, value: user.id }))
  );
};

// Actions

export const readUsersAsync = createAsyncThunk("user/read", async payload => {
  try {
    const response = await axios.get(`/api/user`, {
      params: payload,
    });
    return { users: response.data };
  } catch (error) {
    console.log(error);
  }
});

export const readUserOptionsAsync = createAsyncThunk("user/readOptions", async payload => {
  try {
    const response = await axios.get(`/api/user-options`);
    return { users: response.data };
  } catch (error) {
    console.log(error);
  }
});

export const upsertUserAsync = createAsyncThunk(
  "user/upsert",
  async (payload, { rejectWithValue }) => {
    try {
      let response;
      response = await axios.post(`/api/user`, {
        ...payload,
      });
      return { user: response.data };
    } catch (error) {
      if ("string" === typeof error.response.data) {
        return rejectWithValue(error.response.data);
      } else {
        return rejectWithValue(error.response.data.errors);
      }
    }
  }
);

export const deleteUserAsync = createAsyncThunk("/user/delete", async payload => {
  await axios.delete(`/api/user/${payload.user.id}`);
  return { id: payload.user.id };
});

export const getUsersCSVAsync = createAsyncThunk("user/csv", async (payload, extra) => {
  //const { filters, sorting } = extra.getState().seller;

  const response = await axios({
    url: "/api/user/export",
    method: "GET",
    responseType: "blob", // important
    //params: { filters, sorting },
  });

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "users.csv");
  document.body.appendChild(link);
  link.click();

  //return response.data;
});

export const userSlice = createSlice({
  name: "user",
  initialState: {
    loading: true,
    error: null,
    generalError: null,
    users: [],
    userOptions: [],
  },
  reducers: {
    clearError: (state, action) => {
      if (state.error) state.error[action.payload.field] = null;
    },
    clearErrors: state => {
      state.error = null;
      state.generalError = null;
    },
  },
  extraReducers: {
    [readUsersAsync.pending]: state => {
      state.loading = true;
    },
    [readUsersAsync.fulfilled]: (state, action) => {
      state.loading = false;
      state.users = action.payload.users;
    },
    [readUsersAsync.rejected]: (state, action) => {
      state.loading = false;
      //state.error = action.error;
    },
    [readUserOptionsAsync.pending]: state => {
      state.loading = true;
    },
    [readUserOptionsAsync.fulfilled]: (state, action) => {
      state.loading = false;
      state.userOptions = action.payload.users;
    },
    [readUserOptionsAsync.rejected]: (state, action) => {
      state.loading = false;
      //state.error = action.error;
    },
    [upsertUserAsync.pending]: state => {
      state.loading = true;
    },
    [upsertUserAsync.fulfilled]: (state, action) => {
      state.loading = false;
      if (action.payload.user[1]) {
        // insert
        if (action.payload.user[0]) state.users.unshift(action.payload.user[0]);
      } else {
        // update
        state.users = (state.users || []).map(user =>
          user.id === action.payload.user[0].id ? action.payload.user[0] : user
        );
      }
    },
    [upsertUserAsync.rejected]: (state, action) => {
      state.loading = false;
      if ("string" === typeof action.payload) {
        state.generalError = action.payload;
      } else {
        state.error = (action.payload || []).reduce((acc, error) => {
          acc[error.path] = `The ${error.path} field cannot be empty`;
          return acc;
        }, {});
      }
    },
    [deleteUserAsync.pending]: state => {
      state.loading = true;
    },
    [deleteUserAsync.fulfilled]: (state, action) => {
      state.loading = false;
      state.users = state.users.filter(user => user.id !== action.payload.id);
    },
    [deleteUserAsync.rejected]: (state, action) => {
      state.loading = false;
      state.error = action.error;
    },
  },
});
export const { clearError, clearErrors } = userSlice.actions;
export default userSlice.reducer;
